import React, { Component } from 'react';
import { listPlans, membershipStatus, formatYMD, textoBeneficios } from '../utils/membershipsApi';
import { loadPosDevices, posDeviceField } from '../utils/posDevices';
import PosDeviceSelect from './PosDeviceSelect';

// 'YYYY-MM-DD' + n días, sin pasar por la zona del browser (Date.UTC como en el backend).
function addDaysYMD(ymd, n) {
    const [y, m, d] = String(ymd || '').split('-').map(Number);
    if (!y || !m || !d) return ymd;
    const dt = new Date(Date.UTC(y, m - 1, d + n));
    const p = (x) => String(x).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

var config = process.env;

// Alta / renovación de una membresía en el mostrador.
//
// Overlay propio y no modal de Bootstrap (el backdrop de Bootstrap sobrevive a la navegación
// y deja la pantalla siguiente congelada). Molde: TournamentCreateModal.
//
// La cédula es la llave: al salir del campo se busca la ficha (/clients/:cedula, que une la
// ficha de facturación y la cuenta web) para completar nombre/apellido/email/celular, y el
// estado de socio (/manage-memberships/status/:cedula) para avisar si es una RENOVACIÓN —
// en ese caso el backend encadena el período nuevo al día siguiente del vigente.
class MembershipSellModal extends Component {

    constructor(props) {
        super(props);
        this.state = {
            plans: [],
            planId: '',
            // "Renovar" desde una fila de la grilla: la cédula ya viene y se busca sola al abrir.
            cedula: (props.renovar && props.renovar.cedula) || '',
            name: '', lastName: '', email: '', phone: '',
            paymentMethod: 'efectivo',
            // Con POS, por cuál lector entra (spec-caja D-03): sin él la membresía quedaba en la
            // caja pero fuera del lote de cada aparato.
            posDevices: [],
            posDeviceId: '',
            courtesy: false,
            startDate: '',
            clientHint: null,
            socioHint: null,
            error: null,
            saving: false,
        };
    }

    async componentDidMount() {
        loadPosDevices().then(({ posDevices, posDeviceId }) => {
            if (!this.unmounted) this.setState({ posDevices, posDeviceId: this.state.posDeviceId || posDeviceId });
        });
        try {
            const data = await listPlans();
            const plans = (data && data.plans) || [];
            // Al renovar se propone el mismo plan, si sigue a la venta; si no, el primero.
            const renovar = this.props.renovar;
            const mismo = renovar && plans.find((p) => String(p.id) === String(renovar.planId));
            this.setState({ plans, planId: mismo ? String(mismo.id) : (plans.length ? String(plans[0].id) : '') });
        } catch (e) {
            this.setState({ error: e.message });
        }
        if (this.state.cedula) this.lookupCliente();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    campo = (k, v) => this.setState({ [k]: v, error: null })

    // Sólo se completan los campos VACÍOS: lo que el operador ya tipeó es el dato más fresco.
    lookupCliente = async () => {
        const cedula = this.state.cedula.replace(/\D/g, '');
        if (!cedula) { this.setState({ clientHint: null, socioHint: null }); return; }
        const headers = { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' };
        try {
            const res = await fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}clients/${encodeURIComponent(cedula)}`, { method: 'GET', headers });
            const data = await res.json();
            const cliente = (data && data.error === 0 && data.data) || null;
            if (!cliente) {
                this.setState({ clientHint: { ok: false, text: 'Cliente nuevo: se crea la ficha con estos datos' } });
            } else {
                const perfil = cliente.profile || {};
                const cuenta = cliente.account || {};
                const completar = (actual, ...candidatos) =>
                    (String(actual || '').trim() ? actual : (candidatos.find((v) => v && String(v).trim()) || ''));
                const name = completar(this.state.name, perfil.firstName, cuenta.firstName);
                const lastName = completar(this.state.lastName, perfil.lastName, cuenta.lastName);
                this.setState({
                    name, lastName,
                    email: completar(this.state.email, perfil.email, cuenta.email),
                    phone: completar(this.state.phone, perfil.phone, cuenta.phone),
                    clientHint: { ok: true, text: `✓ ${`${name} ${lastName}`.trim() || 'Cliente encontrado'}${cuenta.id ? ' · tiene cuenta web' : ''}` },
                });
            }
        } catch (e) {
            this.setState({ clientHint: null });
        }
        try {
            const st = await membershipStatus(cedula);
            // "Ya es socio" y "tiene un período comprado que todavía no empezó" son cosas
            // distintas y `coveredUntil` cubre las dos: decir "ya es socio" sobre alguien que
            // hoy no lo es le hace tomar la decisión equivocada al mostrador. El backend
            // encadena desde `coveredUntil`, así que el aviso dice la fecha exacta.
            if (st && st.coveredUntil) {
                const desde = formatYMD(addDaysYMD(st.coveredUntil, 1));
                this.setState({
                    socioHint: {
                        ok: true,
                        text: st.active
                            ? `Ya es socio hasta el ${formatYMD(st.coveredUntil)}: el período nuevo arranca el ${desde}.`
                            : `Tiene una membresía comprada hasta el ${formatYMD(st.coveredUntil)} (todavía no arrancó): el período nuevo arranca el ${desde}.`,
                    },
                });
            } else if (st && st.history && st.history.length) {
                this.setState({ socioHint: { ok: false, text: 'Fue socio y venció: el período nuevo arranca hoy.' } });
            } else {
                this.setState({ socioHint: null });
            }
        } catch (e) {
            this.setState({ socioHint: null });
        }
    }

    vender = async () => {
        if (this.state.saving) return;
        const cedula = this.state.cedula.replace(/\D/g, '');
        if (!this.state.planId) { this.setState({ error: 'Elegí un plan.' }); return; }
        if (!cedula) { this.setState({ error: 'La cédula es obligatoria: es lo que prueba la membresía.' }); return; }
        if (!this.state.name.trim() || !this.state.lastName.trim()) { this.setState({ error: 'Nombre y apellido son obligatorios.' }); return; }

        this.setState({ saving: true, error: null });
        try {
            await this.props.onSell({
                planId: Number(this.state.planId),
                customer: {
                    cedula,
                    name: this.state.name.trim(),
                    lastName: this.state.lastName.trim(),
                    email: this.state.email.trim() || undefined,
                    phone: this.state.phone.trim() || undefined,
                },
                paymentMethod: this.state.courtesy ? undefined : this.state.paymentMethod,
                ...posDeviceField(!this.state.courtesy && this.state.paymentMethod === 'pos', this.state.posDeviceId),
                courtesy: this.state.courtesy,
                startDate: this.state.startDate || undefined,
            });
        } catch (e) {
            this.setState({ error: e.message, saving: false });
        }
    }

    render() {
        const plan = this.state.plans.find((p) => String(p.id) === String(this.state.planId));
        const precio = plan ? Number(plan.price) : 0;
        const gratis = this.state.courtesy || precio === 0;
        return (
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(15,23,42,.45)',
                zIndex: 1080, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '6vh 16px', overflowY: 'auto',
            }} onClick={this.props.onCancel}>
                <div className="card card-small" style={{ width: 560, maxWidth: '100%' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0">{this.props.renovar ? 'Renovar membresía' : 'Nueva membresía'}</h6>
                        <button type="button" className="btn btn-sm btn-white" onClick={this.props.onCancel}>Cancelar</button>
                    </div>
                    <div className="card-body">
                        {!this.state.plans.length ? (
                            <div style={{ background: '#FFF4E5', borderRadius: '.25rem', padding: '8px 10px', fontSize: 12.5, marginBottom: 14 }}>
                                No hay planes activos. Cargalos en Configuraciones → Membresías.
                            </div>
                        ) : null}

                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>Cédula *</label>
                                <input type="text" className="form-control" autoFocus value={this.state.cedula}
                                    placeholder="4.123.456-7"
                                    onChange={(e) => this.setState({ cedula: e.target.value, clientHint: null, socioHint: null, error: null })}
                                    onBlur={this.lookupCliente} />
                                {this.state.clientHint && (
                                    <small className={this.state.clientHint.ok ? 'text-success' : 'text-muted'}>{this.state.clientHint.text}</small>
                                )}
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Celular</label>
                                <input type="text" className="form-control" value={this.state.phone}
                                    onChange={(e) => this.campo('phone', e.target.value)} />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Nombre *</label>
                                <input type="text" className="form-control" value={this.state.name}
                                    onChange={(e) => this.campo('name', e.target.value)} />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Apellido *</label>
                                <input type="text" className="form-control" value={this.state.lastName}
                                    onChange={(e) => this.campo('lastName', e.target.value)} />
                            </div>
                            <div className="col-md-12 form-group">
                                <label>Email</label>
                                <input type="email" className="form-control" value={this.state.email}
                                    onChange={(e) => this.campo('email', e.target.value)} />
                                <small className="text-muted">Con el mismo email de su cuenta web, el socio ve la membresía en su perfil y compra entradas de socio online.</small>
                            </div>
                        </div>

                        {this.state.socioHint && (
                            <div style={{
                                background: this.state.socioHint.ok ? '#EEF4FF' : '#F5F5F5', borderRadius: '.25rem',
                                padding: '8px 10px', fontSize: 12.5, marginBottom: 14, lineHeight: 1.45,
                            }}>{this.state.socioHint.text}</div>
                        )}

                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>Plan *</label>
                                <select className="form-control" value={this.state.planId}
                                    onChange={(e) => this.campo('planId', e.target.value)}>
                                    {!this.state.plans.length && <option value="">Sin planes</option>}
                                    {this.state.plans.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name} — ${Number(p.price)} · {p.duration_months === 1 ? '1 mes' : `${p.duration_months} meses`} · {textoBeneficios(p)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Empieza</label>
                                <input type="date" className="form-control" value={this.state.startDate}
                                    onChange={(e) => this.campo('startDate', e.target.value)} />
                                <small className="text-muted">Vacío = hoy (o cuando venza la vigente).</small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={this.state.courtesy}
                                    onChange={(e) => this.setState({ courtesy: e.target.checked, error: null })} />
                                {' '}Cortesía (sin cobro)
                            </label>
                        </div>
                        {!gratis && (
                            <div className="form-group">
                                <label>Método de pago</label>
                                <select className="form-control" value={this.state.paymentMethod}
                                    onChange={(e) => this.campo('paymentMethod', e.target.value)}>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia</option>
                                    <option value="pos">POS</option>
                                    <option value="mercadopago">Mercado Pago</option>
                                </select>
                                {this.state.paymentMethod === 'pos' && (
                                    <PosDeviceSelect className="form-control mt-2" devices={this.state.posDevices}
                                        value={this.state.posDeviceId} onChange={(posDeviceId) => this.setState({ posDeviceId })} />
                                )}
                            </div>
                        )}

                        {this.state.error ? (
                            <div style={{ background: '#FDECEA', color: '#8B2A20', borderRadius: '.25rem', padding: '8px 10px', fontSize: 13, marginBottom: 12 }}>
                                {this.state.error}
                            </div>
                        ) : null}
                        <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <button type="button" className="btn btn-accent" disabled={this.state.saving || !this.state.plans.length} onClick={this.vender}>
                                {this.state.saving ? 'Registrando…' : (gratis ? 'Registrar sin cobro' : `Cobrar $${precio}`)}
                            </button>
                            <span className="text-muted" style={{ fontSize: 12.5 }}>
                                {gratis ? 'Queda registrada como cortesía.' : 'Genera la venta (canal Membresías) y el movimiento de caja.'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default MembershipSellModal;
